import { identity, keyBy, maxBy, minBy, range, uniqWith } from 'es-toolkit';
import { castDraft, Draft, produce } from 'immer';

import {
  BasicProfile,
  ComponentSide,
  HeelSettings,
  Profile,
  ProfileJoint,
  ProfileLine,
  ProfileLineType,
  ProfileTrussSettings,
  WebJoint,
  WebJointSegment,
} from '@paragon/contract-library';
import {
  convertRadiansToDegrees,
  Direction2D,
  distanceEquals,
  distanceGreaterThan,
  distanceGreaterThanOrEqual,
  distanceLessThan,
  distanceLessThanOrEqual,
  Line2D,
  LineSegment2D,
  Point2D,
  Polygon2D,
  Span,
} from '@paragon/geometry-library';
import { assertExhaustiveSwitch, notNullish, pairwise, secantOfSlope, sortBy } from '@paragon/utilities';

import { ProfileClass } from './ProfileClass';

export function findNeighboringJointGuids(profileLine: ProfileLine, fixedJoint: WebJoint): [string, string] {
  const webJointSegments = profileLine.webJointSegments.filter(
    (webJointSegment) =>
      webJointSegment.baseJointGuid === fixedJoint.guid || webJointSegment.endJointGuid === fixedJoint.guid,
  );
  if (webJointSegments.length !== 2) console.error('Expected to find two web joint segments.');

  const neighboringJointGuids = webJointSegments
    .flatMap((webJointSegment) => [webJointSegment.baseJointGuid, webJointSegment.endJointGuid])
    .filter((guid) => guid !== fixedJoint.guid);
  if (neighboringJointGuids.length !== 2) console.error('Expected to find two neighboring joints.');

  return [neighboringJointGuids[0], neighboringJointGuids[1]];
}

export function clampWebJointX(neighborJoint1X: number, neighborJoint2X: number, newX: number) {
  const [minX, maxX] =
    neighborJoint1X <= neighborJoint2X ? [neighborJoint1X, neighborJoint2X] : [neighborJoint2X, neighborJoint1X];
  if (maxX - minX < 1 / 8) {
    console.log("We probably shouldn't allow segments to be this short");
    return Math.max(minX, Math.min(newX, maxX));
  }
  return Math.max(minX + 1 / 16, Math.min(newX, maxX - 1 / 16));
}

export function getNextJointLocation(
  previousJointLocation: { x: number; y: number },
  distance: number,
  slope: number,
  span: number,
) {
  const isVertical = slope >= 90 || slope <= -90;
  const newX = isVertical ? previousJointLocation.x : Math.max(0, Math.min(distance + previousJointLocation.x, span));
  const yOffset = isVertical ? distance * (slope >= 90 ? 1 : -1) : (newX - previousJointLocation.x) * (slope / 12);
  return {
    x: newX,
    y: yOffset + previousJointLocation.y,
  };
}

export function getHeelHeightBasedOnSlope(heelSettings: HeelSettings, slope: number, topChordWidth: number) {
  switch (heelSettings.heelMethod) {
    case 'HeelHeight':
      return heelSettings.heelHeight;
    case 'ButtCut':
      const realSlope = slope / 12;
      return topChordWidth * secantOfSlope(realSlope) + heelSettings.buttCut;
    default:
      assertExhaustiveSwitch(heelSettings);
  }
}

export function getHeelHeightBasedOnAdjacentTopChordPoint(
  profile: ProfileClass,
  topChordPoint: Point2D,
  topChordWidth: number,
  side: ComponentSide,
) {
  const heelSettings = side === 'Left' ? profile.profileData.leftHeelSettings : profile.profileData.rightHeelSettings;

  switch (heelSettings.heelMethod) {
    case 'HeelHeight':
      return heelSettings.heelHeight;
    case 'ButtCut':
      const minHeelHeight = heelSettings.buttCut + topChordWidth;
      if (
        (side === 'Left' && distanceLessThanOrEqual(topChordPoint.x, 0)) ||
        (side === 'Right' && distanceGreaterThanOrEqual(topChordPoint.x, profile.span)) ||
        distanceLessThan(topChordPoint.y, minHeelHeight)
      ) {
        throw new Error('Cannot get heel height for top chord point that is not in acceptable range.');
      }

      const buttCutPoint = new Point2D(side === 'Left' ? 0 : profile.span, heelSettings.buttCut);
      const rotationSign = side === 'Left' ? -1 : 1;
      const topChordDirection = Direction2D.fromPoints(topChordPoint, buttCutPoint).rotate(
        rotationSign * convertRadiansToDegrees(Math.asin(topChordWidth / topChordPoint.distanceTo(buttCutPoint))),
      );
      const topLine = new Line2D(topChordPoint, topChordDirection);
      const intersection = topLine.intersectWithLine(new Line2D(buttCutPoint, Direction2D.up));
      if (!intersection) throw new Error('Expected intersection to exist.');
      return intersection.y;
    default:
      assertExhaustiveSwitch(heelSettings);
  }
}

interface AdditiveProfileChange {
  linesToAdd: ProfileLine[];
  jointsToAdd: ProfileJoint[];
}

export function applyProfileChange(profile: Profile, changes: AdditiveProfileChange | undefined) {
  if (changes === undefined) return undefined;

  const { profileLines, profileJoints } = profile;
  const { linesToAdd, jointsToAdd } = changes;

  const updatedProfileLines = [...profileLines, ...linesToAdd];
  const updatedProfileJoints = [...profileJoints, ...jointsToAdd];

  return { ...profile, profileLines: updatedProfileLines, profileJoints: updatedProfileJoints };
}

export function addNextLineToProfileFromUserInput(
  targetInfo: { profile: ProfileClass; topChordWidth: number },
  userInputValues: { distance: number; slope: number },
): Profile | undefined {
  const { profile, topChordWidth } = targetInfo;
  const { distance, slope } = userInputValues;

  const nextLineInfo = prepareForNextLine(profile, distance, slope, topChordWidth);
  const updatedProfile = applyProfileChange(profile.profileData, nextLineInfo);

  if (nextLineInfo === undefined || updatedProfile === undefined) return undefined;

  const { baseJoint, nextProfileLineType } = nextLineInfo;
  const endPoint = getNextJointLocation(baseJoint.location, distance, slope, profile.span);
  const existingEndJoint = updatedProfile.profileJoints.find(
    (joint) => distanceEquals(endPoint.x, joint.location.x) && distanceEquals(endPoint.y, joint.location.y),
  );
  if (baseJoint.guid === existingEndJoint?.guid) return undefined;
  const endJoint = existingEndJoint ?? { guid: crypto.randomUUID(), location: endPoint };
  if (!existingEndJoint) updatedProfile.profileJoints = [...updatedProfile.profileJoints, endJoint];
  const fixedJointsForNextLine = getFixedWebJointsForPitchBreaks(
    updatedProfile,
    nextProfileLineType,
    baseJoint,
    endJoint,
  );
  const newProfileLine = createProfileLine(nextProfileLineType, baseJoint, endJoint, fixedJointsForNextLine);
  updatedProfile.profileLines = [...updatedProfile.profileLines, newProfileLine];

  const [finalProfileLines, finalProfileJoints] = getFinalLine(
    { ...profile.settingsData, profile: updatedProfile },
    newProfileLine,
    endJoint,
  );
  updatedProfile.profileLines = [...updatedProfile.profileLines, ...finalProfileLines];
  updatedProfile.profileJoints = [...updatedProfile.profileJoints, ...finalProfileJoints];

  return updatedProfile;
}

/**
 * The foundational data needed in order to add new line(s) to a profile based on user input. Contains any preliminary
 *   changes needed to the profile (by virtue of extending AdditiveProfileChange), the base joint on which the new line
 *   should be based, and the type of the new line.
 */
interface NextLineInfo extends AdditiveProfileChange {
  baseJoint: ProfileJoint;
  nextProfileLineType: ProfileLineType;
}

export function prepareForNextLine(
  profile: ProfileClass,
  distance: number,
  slope: number,
  topChordWidth: number,
): NextLineInfo | undefined {
  const lastProfileLine = profile.getLastProfileLine();

  switch (profile.workingHalf) {
    case 'Top':
      if (!lastProfileLine) {
        const originJoint = { guid: crypto.randomUUID(), location: { x: 0, y: 0 } };

        if (slope >= 90 || slope <= -90) {
          return {
            linesToAdd: [],
            jointsToAdd: [originJoint],
            baseJoint: originJoint,
            nextProfileLineType: 'Heel',
          };
        }

        const leftHeelHeight = getHeelHeightBasedOnSlope(profile.profileData.leftHeelSettings, slope, topChordWidth);

        if (distanceEquals(leftHeelHeight, 0)) {
          return {
            linesToAdd: [],
            jointsToAdd: [originJoint],
            baseJoint: originJoint,
            nextProfileLineType: 'TopChord',
          };
        }

        const heelEndPoint = getNextJointLocation({ x: 0, y: 0 }, leftHeelHeight, 90, profile.span);

        const heelEndJoint = { guid: crypto.randomUUID(), location: heelEndPoint };
        const heelLine = createProfileLine('Heel', originJoint, heelEndJoint);

        return {
          linesToAdd: [heelLine],
          jointsToAdd: [originJoint, heelEndJoint],
          baseJoint: heelEndJoint,
          nextProfileLineType: 'TopChord',
        };
      }

      return {
        linesToAdd: [],
        jointsToAdd: [],
        baseJoint: lastProfileLine.endJoint,
        nextProfileLineType: 'TopChord',
      };
    case 'Bottom':
      if (!lastProfileLine) throw new Error('Cannot work on bottom half of empty profile');

      // The user might put in a horizontal line that is 5.5" line without changing the seat cut setting to be 5.5".
      // So for practical purposes we just assume if the user adds a profile line that is <= 6" on the far left or
      // right of the profile that they intended to put in a seat cut since we don't actually generate a bottom chord
      // for the line if it's 6" or shorter (based on experimentation).
      const maxLeftSeatCutLength = Math.max(profile.settingsData.leftSeatCut, 6);
      const maxRightSeatCutCutLength = Math.max(profile.settingsData.rightSeatCut, 6);

      if (lastProfileLine.profileLineType === 'SeatCut' || lastProfileLine.profileLineType === 'BottomChord') {
        // Check whether the next line should be a bottom chord or seat cut.
        const nextPoint = getNextJointLocation(lastProfileLine.endPoint, distance, slope, profile.span);
        const nextProfileLineType =
          distanceEquals(lastProfileLine.endPoint.y, nextPoint.y) &&
          distanceEquals(nextPoint.x, profile.span) &&
          distanceLessThanOrEqual(Math.abs(nextPoint.x - lastProfileLine.endPoint.x), maxRightSeatCutCutLength)
            ? 'SeatCut'
            : 'BottomChord';

        return {
          linesToAdd: [],
          jointsToAdd: [],
          baseJoint: lastProfileLine.endJoint,
          nextProfileLineType,
        };
      }

      const originJoint = profile.leftHeel?.baseJoint ?? profile.topChords[0]!.baseJoint;
      if (slope === 0) {
        return {
          linesToAdd: [],
          jointsToAdd: [],
          baseJoint: originJoint,
          nextProfileLineType: distanceLessThanOrEqual(distance, maxLeftSeatCutLength) ? 'SeatCut' : 'BottomChord',
        };
      }

      const seatCutEndPoint = getNextJointLocation({ x: 0, y: 0 }, profile.settingsData.leftSeatCut, 0, profile.span);
      const seatCutEndJoint = { guid: crypto.randomUUID(), location: seatCutEndPoint };
      const seatCutLine = createProfileLine('SeatCut', originJoint, seatCutEndJoint);

      return {
        linesToAdd: [seatCutLine],
        jointsToAdd: [seatCutEndJoint],
        baseJoint: seatCutEndJoint,
        nextProfileLineType: 'BottomChord',
      };
    case null:
      return undefined;
    default:
      assertExhaustiveSwitch(profile.workingHalf);
  }
}

export function getFinalLine(
  profileTrussSettings: ProfileTrussSettings,
  profileLine: ProfileLine,
  endJoint: ProfileJoint,
): [ProfileLine[], ProfileJoint[]] {
  const { profile, rightSeatCut, span } = profileTrussSettings;

  if (
    profileLine.profileLineType === 'TopChord' &&
    distanceGreaterThanOrEqual(endJoint.location.x, span) &&
    distanceGreaterThan(endJoint.location.y, 0)
  ) {
    const newJoint = { guid: crypto.randomUUID(), location: { x: endJoint.location.x, y: 0 } };
    const heelLine = createProfileLine('Heel', endJoint, newJoint);
    return [[heelLine], [newJoint]];
  }
  if (
    profileLine.profileLineType === 'BottomChord' &&
    distanceGreaterThanOrEqual(endJoint.location.x, span - rightSeatCut) &&
    distanceLessThan(endJoint.location.x, span) &&
    distanceEquals(endJoint.location.y, 0)
  ) {
    const rightMostJoint = maxBy(profile.profileJoints, (joint) => joint.location.x - joint.location.y);
    console.assert(distanceEquals(rightMostJoint!.location.y, endJoint.location.y), 'Seat cut joints should be level.');
    const seatCutLine = createProfileLine('SeatCut', endJoint, rightMostJoint!);
    return [[seatCutLine], []];
  }
  return [[], []];
}

export function getLinesToCloseProfile(
  profile: ProfileClass,
  topChordWidth: number,
): AdditiveProfileChange | undefined {
  const lastProfileLine = profile.getLastProfileLine();
  if (!lastProfileLine) return undefined;

  switch (profile.workingHalf) {
    case 'Top':
      console.assert(
        lastProfileLine.profileLineType === 'Heel' || lastProfileLine.profileLineType === 'TopChord',
        'The last profile line should be a heel or top chord',
      );

      const spanJoint = { guid: crypto.randomUUID(), location: { x: profile.span, y: 0 } };

      if (distanceGreaterThanOrEqual(lastProfileLine.endPoint.x, profile.span)) {
        const heelLine = createProfileLine('Heel', lastProfileLine.endJoint, spanJoint);
        return {
          linesToAdd: [heelLine],
          jointsToAdd: [],
        };
      }

      const minRightHeelHeight = getHeelHeightBasedOnSlope(profile.profileData.rightHeelSettings, 0, topChordWidth);
      if (distanceLessThan(lastProfileLine.endPoint.y, minRightHeelHeight)) return undefined;

      const rightHeelHeight = getHeelHeightBasedOnAdjacentTopChordPoint(
        profile,
        lastProfileLine.endPoint,
        topChordWidth,
        'Right',
      );

      if (distanceEquals(rightHeelHeight, 0)) {
        const topChordLine = createProfileLine('TopChord', lastProfileLine.endJoint, spanJoint);
        return {
          linesToAdd: [topChordLine],
          jointsToAdd: [spanJoint],
        };
      }

      const heelBasePoint = {
        x: profile.span,
        y: rightHeelHeight,
      };

      const heelBaseJoint = { guid: crypto.randomUUID(), location: heelBasePoint };
      const heelLine = createProfileLine('Heel', heelBaseJoint, spanJoint);
      const topChordLine = createProfileLine('TopChord', lastProfileLine.endJoint, heelBaseJoint);

      return {
        linesToAdd: [topChordLine, heelLine],
        jointsToAdd: [heelBaseJoint, spanJoint],
      };
    case 'Bottom':
      const leftMostJoint = profile.leftHeel?.baseJoint ?? profile.topChords.at(0)!.baseJoint;
      const rightMostJoint = profile.rightHeel?.endJoint ?? profile.topChords.at(-1)!.endJoint;
      const startXForSeatCut = profile.span - profile.settingsData.rightSeatCut;

      if (
        lastProfileLine.profileLineType === 'BottomChord' &&
        // There's room for a seat cut.
        distanceLessThanOrEqual(lastProfileLine.endPoint.x, startXForSeatCut) &&
        // We can't put in a seat cut if the distance left is equal to the seat cut length and the Y is not at 0 since
        // then the seat cut won't be level.
        (distanceEquals(lastProfileLine.endPoint.x, startXForSeatCut) || !distanceEquals(lastProfileLine.endPoint.y, 0))
      ) {
        // There's only room for a seat cut.
        if (distanceEquals(lastProfileLine.endPoint.x, startXForSeatCut)) {
          console.assert(
            distanceEquals(rightMostJoint.location.y, lastProfileLine.endPoint.y),
            'Seat cut joints should be level.',
          );
          const seatCutLine = createProfileLine('SeatCut', lastProfileLine.endJoint, rightMostJoint);
          return { linesToAdd: [seatCutLine], jointsToAdd: [] };
        }

        const seatCutBaseJoint = { guid: crypto.randomUUID(), location: { x: startXForSeatCut, y: 0 } };
        console.assert(
          distanceEquals(rightMostJoint.location.y, seatCutBaseJoint.location.y),
          'Seat cut joints should be level.',
        );
        const fixedJointsForNextLine = getFixedWebJointsForPitchBreaks(
          profile.profileData,
          'BottomChord',
          lastProfileLine.endJoint,
          seatCutBaseJoint,
        );
        const bottomChordLine = createProfileLine(
          'BottomChord',
          lastProfileLine.endJoint,
          seatCutBaseJoint,
          fixedJointsForNextLine,
        );
        const seatCutLine = createProfileLine('SeatCut', seatCutBaseJoint, rightMostJoint);
        return { linesToAdd: [bottomChordLine, seatCutLine], jointsToAdd: [seatCutBaseJoint] };
      }

      const baseJoint =
        lastProfileLine.profileLineType === 'TopChord' || lastProfileLine.profileLineType === 'Heel'
          ? leftMostJoint
          : lastProfileLine.endJoint;

      const fixedJointsForNextLine = getFixedWebJointsForPitchBreaks(
        profile.profileData,
        'BottomChord',
        baseJoint,
        rightMostJoint,
      );
      const bottomChordLine = createProfileLine('BottomChord', baseJoint, rightMostJoint, fixedJointsForNextLine);

      return { linesToAdd: [bottomChordLine], jointsToAdd: [] };
    case null:
      return undefined;
    default:
      assertExhaustiveSwitch(profile.workingHalf);
  }
}

export function updateFeatureAndFloatingJointsOnProfile<T extends BasicProfile>(profile: T): T {
  const profileWithUpdatedFeatureJoints = updateFeatureJointsOnProfile(profile);
  return updateFloatingJointsOnProfile(profileWithUpdatedFeatureJoints);
}

function updateFeatureJointsOnProfile<T extends BasicProfile>(profile: T): T {
  return produce(profile, (draftProfile) => {
    let oldFeatureJoints = draftProfile.profileLines.flatMap((profileLine) =>
      profileLine.featureJoints.map((featureJoint) => ({ profileLine, featureJoint })),
    );
    let newFeatureJoints = calculateFeatureJoints(draftProfile);

    let matchedFeatureJoints: { location: Point2D; profileLine: ProfileLine }[] = [];
    for (const newFeatureJoint of newFeatureJoints) {
      let matchingOldFeatureJoint: WebJoint | null = null;
      for (const oldFeatureJoint of oldFeatureJoints) {
        if (
          newFeatureJoint.profileLine.guid === oldFeatureJoint.profileLine.guid &&
          distanceEquals(newFeatureJoint.location.x, oldFeatureJoint.featureJoint.x)
        ) {
          matchedFeatureJoints.push(newFeatureJoint);
          matchingOldFeatureJoint = oldFeatureJoint.featureJoint;
          break;
        }
      }
      oldFeatureJoints = oldFeatureJoints.filter((joint) => joint.featureJoint.guid !== matchingOldFeatureJoint?.guid);
    }
    newFeatureJoints = newFeatureJoints.filter((joint) => !matchedFeatureJoints.includes(joint));

    matchedFeatureJoints = [];
    for (const newFeatureJoint of newFeatureJoints) {
      const oldFeatureJointsWithSameNeighboringJoints = oldFeatureJoints
        .filter((oldFeatureJoint) => oldFeatureJoint.profileLine.guid === newFeatureJoint.profileLine.guid)
        .filter((oldFeatureJoint) => {
          const webJointSegmentsThatNeighborOldFeatureJoint = oldFeatureJoint.profileLine.webJointSegments.filter(
            (webJointSegment) =>
              webJointSegment.baseJointGuid === oldFeatureJoint.featureJoint.guid ||
              webJointSegment.endJointGuid === oldFeatureJoint.featureJoint.guid,
          );
          if (webJointSegmentsThatNeighborOldFeatureJoint.length !== 2)
            throw new Error('Expected two web joint segments.');
          const oldFeatureJointNeighboringJointGuids = webJointSegmentsThatNeighborOldFeatureJoint.map(
            (webJointSegment) =>
              webJointSegment.baseJointGuid === oldFeatureJoint.featureJoint.guid
                ? webJointSegment.endJointGuid
                : webJointSegment.baseJointGuid,
          );
          if (oldFeatureJointNeighboringJointGuids.length !== 2) throw new Error('Expected two neighboring joints.');
          const webJointSegmentsThatContainNewFeatureJointLocation =
            newFeatureJoint.profileLine.webJointSegments.filter((webJointSegment) =>
              calculateWebJointSegmentGeometry(profile, newFeatureJoint.profileLine, webJointSegment).contains(
                newFeatureJoint.location,
              ),
            );
          if (webJointSegmentsThatContainNewFeatureJointLocation.length !== 1)
            throw new Error('Expected only one web joint segment to be at this location.');
          const webJointSegment = webJointSegmentsThatContainNewFeatureJointLocation[0];
          const newFeatureJointNeighboringJointGuids = [webJointSegment.baseJointGuid, webJointSegment.endJointGuid];
          return oldFeatureJointNeighboringJointGuids.every((guid) =>
            newFeatureJointNeighboringJointGuids.includes(guid),
          );
        });
      const closestOldFeatureJointWithSameNeighboringJoints = minBy(
        oldFeatureJointsWithSameNeighboringJoints,
        (oldFeatureJoint) => Math.abs(oldFeatureJoint.featureJoint.x - newFeatureJoint.location.x),
      );
      if (closestOldFeatureJointWithSameNeighboringJoints) {
        matchedFeatureJoints.push(newFeatureJoint);
        oldFeatureJoints = oldFeatureJoints.filter(
          (joint) => joint.featureJoint.guid !== closestOldFeatureJointWithSameNeighboringJoints.featureJoint.guid,
        );
        closestOldFeatureJointWithSameNeighboringJoints.featureJoint.x = newFeatureJoint.location.x;
      }
    }
    newFeatureJoints = newFeatureJoints.filter((joint) => !matchedFeatureJoints.includes(joint));

    for (const { featureJoint: featureJointToRemove, profileLine } of oldFeatureJoints) {
      profileLine.featureJoints = profileLine.featureJoints.filter(
        (featureJoint) => featureJoint.guid !== featureJointToRemove.guid,
      );
      const webJointSegmentsToCombine = profileLine.webJointSegments.filter(
        (webJointSegment) =>
          webJointSegment.baseJointGuid === featureJointToRemove.guid ||
          webJointSegment.endJointGuid === featureJointToRemove.guid,
      );
      if (webJointSegmentsToCombine.length !== 2) throw new Error('Expected two web joint segments to exist.');
      profileLine.webJointSegments = profileLine.webJointSegments.filter((webJointSegment) =>
        webJointSegmentsToCombine.every(
          (webJointSegmentToCombine) => webJointSegmentToCombine.guid !== webJointSegment.guid,
        ),
      );
      const jointGuids = webJointSegmentsToCombine
        .flatMap((webJointSegment) => [webJointSegment.baseJointGuid, webJointSegment.endJointGuid])
        .filter((jointGuid) => jointGuid !== featureJointToRemove.guid);
      if (jointGuids.length !== 2) throw new Error('Expected two joints.');
      profileLine.webJointSegments.push({
        guid: crypto.randomUUID(),
        baseJointGuid: jointGuids[0],
        endJointGuid: jointGuids[1],
        numberOfFloatingJoints: null,
        floatingJoints: [],
      });
    }

    for (const { location, profileLine } of newFeatureJoints) {
      const webJointSegmentsThatContainNewFeatureJointLocation = profileLine.webJointSegments.filter(
        (webJointSegment) => calculateWebJointSegmentGeometry(profile, profileLine, webJointSegment).contains(location),
      );
      if (webJointSegmentsThatContainNewFeatureJointLocation.length !== 1)
        throw new Error('Expected only one web joint segment to be at this location.');
      const webJointSegmentThatContainsNewFeatureJoint = webJointSegmentsThatContainNewFeatureJointLocation[0];
      const newFeatureJoint = {
        guid: crypto.randomUUID(),
        x: location.x,
      };
      profileLine.featureJoints.push(newFeatureJoint);
      profileLine.webJointSegments = profileLine.webJointSegments.filter(
        (webJointSegment) => webJointSegment.guid !== webJointSegmentThatContainsNewFeatureJoint.guid,
      );
      profileLine.webJointSegments.push({
        guid: crypto.randomUUID(),
        baseJointGuid: webJointSegmentThatContainsNewFeatureJoint.baseJointGuid,
        endJointGuid: newFeatureJoint.guid,
        numberOfFloatingJoints: null,
        floatingJoints: [],
      });
      profileLine.webJointSegments.push({
        guid: crypto.randomUUID(),
        baseJointGuid: newFeatureJoint.guid,
        endJointGuid: webJointSegmentThatContainsNewFeatureJoint.endJointGuid,
        numberOfFloatingJoints: null,
        floatingJoints: [],
      });
    }
  });
}

function calculateFeatureJoints(profile: Draft<BasicProfile>) {
  const profileLinesWithGeometries = profile.profileLines
    .filter(
      (profileLine) => profileLine.profileLineType === 'TopChord' || profileLine.profileLineType === 'BottomChord',
    )
    .map((profileLine) => ({ profileLine, geometry: calculateProfileLineGeometry(profile, profileLine) }));
  const verticalProfileLines = profileLinesWithGeometries.filter(({ geometry }) =>
    distanceEquals(geometry.basePoint.x, geometry.endPoint.x),
  );
  return verticalProfileLines
    .map(({ profileLine, geometry }) => {
      const intersections = profileLinesWithGeometries
        .filter((pair) => pair.profileLine.profileLineType !== profileLine.profileLineType)
        .map((pair) => {
          const intersection = pair.geometry.intersectWithLine(geometry.toLine());
          if (!intersection || pair.geometry.endPoints.some(intersection.equals.bind(intersection))) return null;
          return { profileLine: pair.profileLine, location: intersection };
        })
        .filter(notNullish);
      if (intersections.length !== 1) return null;
      return { ...intersections[0], location: intersections[0].location! };
    })
    .filter(notNullish)
    .filter((pair) =>
      pair.profileLine.fixedJoints.every((fixedJoint) => !distanceEquals(fixedJoint.x, pair.location.x)),
    );
}

const updateFloatingJointsOnProfile = <T extends BasicProfile>(profile: T): T => {
  const newLines = profile.profileLines.map((line) => {
    const newWebJointSegments = line.webJointSegments.map((webJointSegment) => {
      const endPoints = profile.profileJoints
        .map((joint) => ({ guid: joint.guid, x: joint.location.x }))
        .concat(line.fixedJoints.concat(line.featureJoints))
        .filter((joint) => joint.guid === webJointSegment.baseJointGuid || joint.guid === webJointSegment.endJointGuid);
      if (endPoints.length !== 2) throw new Error();
      const floatingJoints = calculateFloatingJoints(
        profile,
        line.profileLineType,
        endPoints[0].x,
        endPoints[1].x,
        webJointSegment.numberOfFloatingJoints,
      );
      return { ...webJointSegment, floatingJoints };
    });
    return { ...line, webJointSegments: newWebJointSegments };
  });
  return { ...profile, profileLines: newLines };
};

function mutablyAdjustHeelHeightOnSide(
  profile: ProfileClass,
  draftProfile: Draft<Profile>,
  heelHeightAdjustment: number,
  side: ComponentSide,
) {
  const heel = side === 'Left' ? profile.leftHeel : profile.rightHeel;

  const bottomJointKey = side === 'Left' ? 'baseJoint' : 'endJoint';
  const topJointKey = side === 'Left' ? 'endJoint' : 'baseJoint';
  const bottomJointGuidKey = `${bottomJointKey}Guid` as const;

  const adjacentTopChordClass = profile.topChords.at(side === 'Left' ? 0 : -1);
  const adjacentTopChord = draftProfile.profileLines.find((line) => line.guid === adjacentTopChordClass?.guid);

  // We have a heel and it should go away.
  if (heel && distanceEquals(heel.length + heelHeightAdjustment, 0)) {
    draftProfile.profileJoints = draftProfile.profileJoints.filter((joint) => joint.guid !== heel[topJointKey].guid);
    draftProfile.profileLines = draftProfile.profileLines.filter((line) => line.guid !== heel.guid);

    if (adjacentTopChord) {
      console.assert(
        adjacentTopChord[bottomJointGuidKey] === adjacentTopChord.webJointSegments[0][bottomJointGuidKey],
        'The joints should match',
      );
      adjacentTopChord[bottomJointGuidKey] = heel[bottomJointKey].guid;
      const adjacentWebJointSegment = adjacentTopChord.webJointSegments.at(side === 'Left' ? 0 : -1)!;
      adjacentWebJointSegment[bottomJointGuidKey] = heel[bottomJointKey].guid;
    }
  }

  // We don't have a heel and we need one.
  if (!heel && !distanceEquals(heelHeightAdjustment, 0)) {
    let heelBottomJoint;
    if (adjacentTopChordClass) {
      heelBottomJoint = adjacentTopChordClass[bottomJointKey];
    } else {
      // This profile is empty.
      console.assert(
        side === 'Left' && draftProfile.profileJoints.length === 0 && draftProfile.profileLines.length === 0,
        'Expected to be on the left with an empty profile.',
      );
      heelBottomJoint = { guid: crypto.randomUUID(), location: Point2D.origin.toObject() };
      draftProfile.profileJoints.push(heelBottomJoint);
    }

    const heelTopJoint = {
      guid: crypto.randomUUID(),
      location: { x: heelBottomJoint.location.x, y: heelBottomJoint.location.y + heelHeightAdjustment },
    };
    draftProfile.profileJoints.push(heelTopJoint);
    draftProfile.profileLines.push(castDraft(createProfileLine('Heel', heelBottomJoint, heelTopJoint)));
    if (adjacentTopChord) {
      adjacentTopChord[bottomJointGuidKey] = heelTopJoint.guid;
      const adjacentWebJointSegment = adjacentTopChord.webJointSegments.at(side === 'Left' ? 0 : -1)!;
      adjacentWebJointSegment[bottomJointGuidKey] = heelTopJoint.guid;
    }
  }
}

function getOuterMostJointOnSide(profile: ProfileClass, side: ComponentSide) {
  const heel = side === 'Left' ? profile.leftHeel : profile.rightHeel;
  const outerMostTopChord = profile.topChords.at(side === 'Left' ? 0 : -1);
  const outerMostJointKey = side === 'Left' ? 'baseJoint' : 'endJoint';

  if (heel) return heel[outerMostJointKey];
  if (outerMostTopChord) return outerMostTopChord[outerMostJointKey];
  return null;
}

function shouldJointBeAdjustedWithHeelAdjustment(profile: ProfileClass, joint: ProfileJoint) {
  // The outermost joints should not be adjusted since they're part of both the top and bottom half of the profile.
  if (
    getOuterMostJointOnSide(profile, 'Left')?.guid === joint.guid ||
    getOuterMostJointOnSide(profile, 'Right')?.guid === joint.guid
  ) {
    return false;
  }

  return profile.topChords.some(
    (topChord) => topChord.baseJoint.guid === joint.guid || topChord.endJoint.guid === joint.guid,
  );
}

function adjustHeelsByOffset(profile: ProfileClass, heelHeightAdjustment: number) {
  return produce(profile.profileData, (draftProfile) => {
    for (const joint of draftProfile.profileJoints) {
      if (shouldJointBeAdjustedWithHeelAdjustment(profile, joint)) joint.location.y += heelHeightAdjustment;
    }

    if (profile.getLeftHeelHeightSource() === 'ProfileGeometry')
      mutablyAdjustHeelHeightOnSide(profile, draftProfile, heelHeightAdjustment, 'Left');
    if (profile.getRightHeelHeightSource() === 'ProfileGeometry')
      mutablyAdjustHeelHeightOnSide(profile, draftProfile, heelHeightAdjustment, 'Right');
  });
}

export function adjustHeelsToDesiredHeelHeight(profile: ProfileClass, newHeelHeight: number, side: ComponentSide) {
  const leftHeelSource = profile.getLeftHeelHeightSource();
  const rightHeelSource = profile.getRightHeelHeightSource();

  if (leftHeelSource === 'ProfileGeometry' && rightHeelSource === 'ProfileGeometry') {
    const oldLeftHeelHeight = profile.leftHeel?.segment.length ?? 0;
    const oldRightHeelHeight = profile.rightHeel?.segment.length ?? 0;

    const heelHeightAdjustment =
      side === 'Left' ? newHeelHeight - oldLeftHeelHeight : newHeelHeight - oldRightHeelHeight;

    if (
      distanceLessThan(oldLeftHeelHeight + heelHeightAdjustment, 0) ||
      distanceLessThan(oldRightHeelHeight + heelHeightAdjustment, 0)
    ) {
      throw new Error('Cannot decrease heel height below zero!');
    }

    return adjustHeelsByOffset(profile, heelHeightAdjustment);
  }

  if (leftHeelSource === 'ProfileGeometry' && side === 'Left') {
    const oldLeftHeelHeight = profile.leftHeel?.segment.length ?? 0;
    if (distanceLessThan(newHeelHeight, 0)) throw new Error('Cannot decrease heel height below zero!');
    return adjustHeelsByOffset(profile, newHeelHeight - oldLeftHeelHeight);
  }

  throw new Error('Expected this method to only be called on a heel defined by its geometry.');
}

export const shiftWebJoint = <T extends BasicProfile>(profile: T, webJointGuid: string, deltaX: number): T =>
  produce(profile, (draftProfile) => {
    for (const profileLine of draftProfile.profileLines) {
      for (const fixedJoint of profileLine.fixedJoints) {
        if (fixedJoint.guid !== webJointGuid) continue;

        const [neighboringJoint1Guid, neighboringJoint2Guid] = findNeighboringJointGuids(profileLine, fixedJoint);
        const neighboringJoint1X = findProfileFixedOrFeatureJointXLocation(
          draftProfile,
          profileLine,
          neighboringJoint1Guid,
        );
        const neighboringJoint2X = findProfileFixedOrFeatureJointXLocation(
          draftProfile,
          profileLine,
          neighboringJoint2Guid,
        );

        fixedJoint.x = clampWebJointX(neighboringJoint1X, neighboringJoint2X, fixedJoint.x + deltaX);
      }

      for (const webJointSegment of profileLine.webJointSegments) {
        for (const floatingJoint of webJointSegment.floatingJoints) {
          if (floatingJoint.guid !== webJointGuid) continue;

          const webJointSegmentGeometry = calculateWebJointSegmentGeometry(profile, profileLine, webJointSegment);
          const neighboringJoint1X = webJointSegmentGeometry.basePoint.x;
          const neighboringJoint2X = webJointSegmentGeometry.endPoint.x;
          profileLine.fixedJoints.push({
            guid: floatingJoint.guid,
            x: clampWebJointX(neighboringJoint1X, neighboringJoint2X, floatingJoint.x + deltaX),
          });

          profileLine.webJointSegments.push({
            guid: crypto.randomUUID(),
            baseJointGuid: floatingJoint.guid,
            endJointGuid: webJointSegment.endJointGuid,
            numberOfFloatingJoints: null,
            floatingJoints: [],
          });

          webJointSegment.endJointGuid = floatingJoint.guid;
          webJointSegment.numberOfFloatingJoints = null;
          webJointSegment.floatingJoints = [];
        }
      }
    }
  });

export const deleteFixedJoint = (jointGuid: string) => (profile: BasicProfile) =>
  produce(profile, (draftProfile) => {
    for (const profileLine of draftProfile.profileLines) {
      if (!profileLine.fixedJoints.some((fixedJoint) => fixedJoint.guid === jointGuid)) continue;

      const leftWebJointSegment = profileLine.webJointSegments.find((segment) => segment.endJointGuid === jointGuid);
      const rightWebJointSegment = profileLine.webJointSegments.find((segment) => segment.baseJointGuid === jointGuid);
      if (!leftWebJointSegment || !rightWebJointSegment) return;
      const newWebJointSegment = {
        baseJointGuid: leftWebJointSegment.baseJointGuid,
        endJointGuid: rightWebJointSegment.endJointGuid,
        guid: crypto.randomUUID(),
        numberOfFloatingJoints: null,
        floatingJoints: [],
      };
      profileLine.webJointSegments = profileLine.webJointSegments
        .filter((segment) => segment.guid !== leftWebJointSegment.guid && segment.guid !== rightWebJointSegment.guid)
        .concat(newWebJointSegment);
      profileLine.fixedJoints = profileLine.fixedJoints.filter((fixedJoint) => fixedJoint.guid !== jointGuid);
    }
  });

export const updateWebJointSegmentPanels =
  (webJointSegmentGuid: string, panels: number | null) => (profile: BasicProfile) =>
    produce(profile, (draftProfile) => {
      for (const profileLine of draftProfile.profileLines) {
        if (!profileLine.webJointSegments.some((segment) => segment.guid === webJointSegmentGuid)) continue;

        for (const draftWebJointSegment of profileLine.webJointSegments) {
          if (draftWebJointSegment.guid !== webJointSegmentGuid) continue;

          draftWebJointSegment.numberOfFloatingJoints = panels === null ? null : panels - 1;
        }
      }
    });

export function createProfileLine(
  profileLineType: ProfileLineType,
  baseJoint: ProfileJoint,
  endJoint: ProfileJoint,
  fixedJoints: readonly WebJoint[] = [],
): ProfileLine {
  return {
    guid: crypto.randomUUID(),
    profileLineType,
    fixedJoints,
    featureJoints: [],
    webJointSegments:
      fixedJoints.length > 0
        ? generateWebJointSegmentsForFixedJoints(fixedJoints, baseJoint, endJoint)
        : [
            {
              guid: crypto.randomUUID(),
              baseJointGuid: baseJoint.guid,
              endJointGuid: endJoint.guid,
              numberOfFloatingJoints: null,
              floatingJoints: [],
            },
          ],
    baseJointGuid: baseJoint.guid,
    endJointGuid: endJoint.guid,
  };
}

function generateWebJointSegmentsForFixedJoints(
  fixedJoints: readonly WebJoint[],
  baseJoint: ProfileJoint,
  endJoint: ProfileJoint,
): WebJointSegment[] {
  if (distanceEquals(baseJoint.location.x, endJoint.location.x)) throw new Error('');
  const profileJoints = [baseJoint, endJoint];
  const lowXJoint = minBy(profileJoints, (joint) => joint.location.x)!;
  const highXJoint = maxBy(profileJoints, (joint) => joint.location.x)!;
  const sortedFixedJoints = sortBy(fixedJoints, (fixedJoint) => fixedJoint.x);
  const jointsPairwise = pairwise([lowXJoint, ...sortedFixedJoints, highXJoint]);
  return jointsPairwise.map(([base, end]) => ({
    guid: crypto.randomUUID(),
    baseJointGuid: base.guid,
    endJointGuid: end.guid,
    numberOfFloatingJoints: null,
    floatingJoints: [],
  }));
}

export function calculateNumberOfFloatingJoints(
  profile: BasicProfile,
  profileLineType: 'TopChord' | 'BottomChord',
  spanLength: number,
) {
  const maxPanelLength =
    profileLineType === 'TopChord' ? profile.topChordMaxPanelLength : profile.bottomChordMaxPanelLength;
  return spanLength === 0 ? 0 : Math.ceil(spanLength / maxPanelLength) - 1;
}

function calculateFloatingJoints(
  profile: BasicProfile,
  profileLineType: ProfileLineType,
  firstJointX: number,
  secondJointX: number,
  specifiedNumberOfFloatingJoints: number | null,
) {
  if (profileLineType === 'Heel' || profileLineType === 'SeatCut') return [];

  const numberOfFloatingJoints =
    specifiedNumberOfFloatingJoints ??
    calculateNumberOfFloatingJoints(profile, profileLineType, Math.abs(secondJointX - firstJointX));
  const startX = Math.min(firstJointX, secondJointX);
  const spanLength = Math.abs(secondJointX - firstJointX);
  const panelLength = spanLength / (numberOfFloatingJoints + 1);

  return range(numberOfFloatingJoints).map((index) => ({
    guid: crypto.randomUUID(),
    x: startX + (index + 1) * panelLength,
  }));
}

export function mutablyUpdateProfileJointLocation(
  draftProfile: Draft<Profile>,
  jointToMoveGuid: string,
  newLocation: { x: number; y: number },
) {
  for (const profileJoint of draftProfile.profileJoints) {
    if (profileJoint.guid !== jointToMoveGuid) continue;
    profileJoint.location = newLocation;
  }
  // Remove fixed joints on neighboring profile lines which are no longer on profile lines
  const changedProfileLines = draftProfile.profileLines.filter((profileLine) =>
    [profileLine.baseJointGuid, profileLine.endJointGuid].includes(jointToMoveGuid),
  );
  for (const changedProfileLine of changedProfileLines) {
    const profileLineGeometry = calculateProfileLineGeometry(draftProfile, changedProfileLine);
    const { basePoint, endPoint } = profileLineGeometry;
    const [leftX, rightX] = basePoint.x < endPoint.x ? [basePoint.x, endPoint.x] : [endPoint.x, basePoint.x];
    const fixedJointGuidsToRemove = changedProfileLine.fixedJoints
      .filter(({ x }) => distanceLessThanOrEqual(x, leftX) || distanceGreaterThanOrEqual(x, rightX))
      .map(({ guid }) => guid);
    if (fixedJointGuidsToRemove.length === 0) continue;
    const middleWebJointSegmentsToRemove = changedProfileLine.webJointSegments.filter(
      (segment) =>
        fixedJointGuidsToRemove.includes(segment.baseJointGuid) &&
        fixedJointGuidsToRemove.includes(segment.endJointGuid),
    );
    const endWebJointSegmentsToRemove = changedProfileLine.webJointSegments.filter(
      (segment) =>
        fixedJointGuidsToRemove.includes(segment.baseJointGuid) !==
        fixedJointGuidsToRemove.includes(segment.endJointGuid),
    );
    const webJointSegmentGuidsToRemove = middleWebJointSegmentsToRemove
      .concat(endWebJointSegmentsToRemove)
      .map(({ guid }) => guid);
    const jointGuidsInNeedOfNewWebJointSegment = endWebJointSegmentsToRemove
      .flatMap((segment) => [segment.baseJointGuid, segment.endJointGuid])
      .filter((guid) => !fixedJointGuidsToRemove.includes(guid));
    if (jointGuidsInNeedOfNewWebJointSegment.length !== 2)
      throw new Error('Could not find where to place new web joint segment');
    const newWebJointSegment: WebJointSegment = {
      baseJointGuid: jointGuidsInNeedOfNewWebJointSegment[0],
      endJointGuid: jointGuidsInNeedOfNewWebJointSegment[1],
      floatingJoints: [],
      guid: crypto.randomUUID(),
      numberOfFloatingJoints: null,
    };
    changedProfileLine.fixedJoints = changedProfileLine.fixedJoints.filter(
      ({ guid }) => !fixedJointGuidsToRemove.includes(guid),
    );
    changedProfileLine.webJointSegments = changedProfileLine.webJointSegments
      .filter(({ guid }) => !webJointSegmentGuidsToRemove.includes(guid))
      .concat(castDraft(newWebJointSegment));
  }
}

export function mutablyReplaceProfileJoint(profile: Draft<Profile>, newJoint: ProfileJoint) {
  const oldJoint = profile.profileJoints.find((existingJoint) => existingJoint.guid === newJoint.guid);
  if (!oldJoint || oldJoint === newJoint) {
    return;
  }

  profile.profileJoints[profile.profileJoints.indexOf(oldJoint)] = newJoint;

  mutablyUpdateProfileJointLocation(profile, newJoint.guid, newJoint.location);
}

export function getProfileIsClosed(profile: BasicProfile | null | undefined) {
  if (!profile || profile.profileLines.length < 3) return false;
  const jointsKeyedByGuid = keyBy(profile.profileJoints, (j) => j.guid);
  const doubledJointLocations = [
    ...profile.profileLines.map((line) => jointsKeyedByGuid[line.endJointGuid].location),
    ...profile.profileLines.map((line) => jointsKeyedByGuid[line.baseJointGuid].location),
  ];
  return doubledJointLocations.every(
    (jointLocation) =>
      doubledJointLocations.filter(
        (otherJointLocation) =>
          distanceEquals(jointLocation.x, otherJointLocation.x) &&
          distanceEquals(jointLocation.y, otherJointLocation.y),
      ).length === 2,
  );
}

export function calculateProfileLineGeometry(profile: BasicProfile, profileLine: ProfileLine) {
  const baseJoint = profile.profileJoints.find((joint) => joint.guid === profileLine.baseJointGuid);
  const endJoint = profile.profileJoints.find((joint) => joint.guid === profileLine.endJointGuid);
  if (!baseJoint || !endJoint) throw new Error('Could not find profile joints associated with profile line.');
  return LineSegment2D.fromPointObjects(baseJoint.location, endJoint.location);
}

export function calculateWebJointLocation(segment: LineSegment2D, webJoint: WebJoint) {
  return segment.intersectWithLine(new Line2D(new Point2D(webJoint.x, 0), new Direction2D(0, 1)))!;
}

export function findProfileFixedOrFeatureJointXLocation(
  profile: BasicProfile,
  profileLine: ProfileLine,
  jointGuid: string,
) {
  const profileJoint = profile.profileJoints.find((joint) => joint.guid === jointGuid);
  if (profileJoint) return profileJoint.location.x;

  const fixedOrFeatureJoint = profileLine.fixedJoints
    .concat(profileLine.featureJoints)
    .find((joint) => joint.guid === jointGuid);
  if (fixedOrFeatureJoint) return fixedOrFeatureJoint.x;

  throw new Error('Could not find profile, fixed, or feature joint.');
}

function calculateProfileFixedOrFeatureJointLocation(
  profile: BasicProfile,
  profileLine: ProfileLine,
  profileLineGeometry: LineSegment2D,
  jointGuid: string,
) {
  const profileJoint = profile.profileJoints.find((joint) => joint.guid === jointGuid);
  if (profileJoint) return Point2D.fromObject(profileJoint.location);
  const fixedOrFeatureJoint = profileLine.fixedJoints
    .concat(profileLine.featureJoints)
    .find((joint) => joint.guid === jointGuid);
  if (fixedOrFeatureJoint) return calculateWebJointLocation(profileLineGeometry, fixedOrFeatureJoint);

  throw new Error('Could not find joints associated with web joint segment.');
}

export function calculateWebJointSegmentGeometry(
  profile: BasicProfile,
  profileLine: ProfileLine,
  webJointSegment: WebJointSegment,
) {
  const profileLineGeometry = calculateProfileLineGeometry(profile, profileLine);
  return new LineSegment2D(
    calculateProfileFixedOrFeatureJointLocation(
      profile,
      profileLine,
      profileLineGeometry,
      webJointSegment.baseJointGuid,
    ),
    calculateProfileFixedOrFeatureJointLocation(
      profile,
      profileLine,
      profileLineGeometry,
      webJointSegment.endJointGuid,
    ),
  );
}

export function findWebJoint(profile: BasicProfile, webJointGuid: string) {
  return profile.profileLines
    .flatMap((profileLine) =>
      profileLine.fixedJoints
        .concat(profileLine.featureJoints)
        .concat(profileLine.webJointSegments.flatMap((webJointSegment) => webJointSegment.floatingJoints)),
    )
    .find((webJoint) => webJoint.guid === webJointGuid);
}

export function findAndCalculateSegmentGeometryAndWebJointLocation(profile: BasicProfile, webJointGuid: string) {
  const fixedOrFeatureJointLocation = profile.profileLines
    .flatMap((profileLine) =>
      profileLine.fixedJoints.concat(profileLine.featureJoints).map((fixedOrFeatureJoint) => {
        if (fixedOrFeatureJoint.guid !== webJointGuid) return null;

        const segmentGeometry = calculateProfileLineGeometry(profile, profileLine);
        return {
          segmentGeometry,
          webJointLocation: calculateWebJointLocation(segmentGeometry, fixedOrFeatureJoint),
        };
      }),
    )
    .find(notNullish);
  if (fixedOrFeatureJointLocation) return fixedOrFeatureJointLocation;

  const floatingJointLocation = profile.profileLines
    .flatMap((profileLine) =>
      profileLine.webJointSegments.flatMap((webJointSegment) =>
        webJointSegment.floatingJoints.map((floatingJoint) => {
          if (floatingJoint.guid !== webJointGuid) return null;

          const segmentGeometry = calculateWebJointSegmentGeometry(profile, profileLine, webJointSegment);
          return {
            segmentGeometry,
            webJointLocation: calculateWebJointLocation(segmentGeometry, floatingJoint),
          };
        }),
      ),
    )
    .find(notNullish);
  if (floatingJointLocation) return floatingJointLocation;

  throw new Error('Could not find web joint.');
}

export function findAndCalculateWebJointLocation(profile: BasicProfile, webJointGuid: string) {
  return findAndCalculateSegmentGeometryAndWebJointLocation(profile, webJointGuid).webJointLocation;
}

export function findProfileLineForWebJointSegment(profile: BasicProfile, webJointSegment: WebJointSegment) {
  return profile.profileLines.find((line) =>
    line.webJointSegments.some((segment) => segment.guid === webJointSegment.guid),
  )!;
}

export function isFullProfile(profile: BasicProfile): profile is Profile {
  const key: Exclude<keyof Profile, keyof BasicProfile> = 'guid'; // Type-safe; will fail compilation if interfaces change
  return key in profile;
}

/**
 * This is the client-side version of `Profile.AddFixedJoints` on the server.
 */
function getFixedWebJointsForPitchBreaks(
  profile: Profile,
  profileLineType: ProfileLineType,
  baseJoint: ProfileJoint,
  endJoint: ProfileJoint,
): readonly WebJoint[] {
  if (distanceEquals(baseJoint.location.x, endJoint.location.x)) return [];

  const profileWithNextLine: Profile = {
    ...profile,
    profileLines: [...profile.profileLines, createProfileLine(profileLineType, baseJoint, endJoint)],
  };

  const pitchBreakXs = getPitchBreakXs(profileWithNextLine);

  if (pitchBreakXs.length < 2) return [];

  const bottomChordProfileLines = profileWithNextLine.profileLines.filter(
    (profileLine) => profileLine.profileLineType === 'BottomChord',
  );

  // Stop processing if there are no bottom chord lines; the profile needs more definition or testing symmetry could
  // fail.
  if (bottomChordProfileLines.length === 0) return [];

  // Filter out pitch breaks that match the base or end point of the profile line so that we do not end up with zero
  // length web joint segments. This consideration is unique to the clientside, where we may be dealing with an
  // incomplete profile.
  const bottomChordProfileLineGeometry = LineSegment2D.fromPointObjects(baseJoint.location, endJoint.location);
  const bottomChordProfileLineSpan = new Span(
    bottomChordProfileLineGeometry.basePoint.x,
    bottomChordProfileLineGeometry.endPoint.x,
  );
  const pitchBreakXsInternalToLine = pitchBreakXs.filter((pitchBreakX) =>
    bottomChordProfileLineSpan.containsOnInside(pitchBreakX),
  );

  // Filter out close pitch breaks that would tend to create undesirable sharp scarf cuts
  const existingBottomChordWebJointXs = uniqWith(
    bottomChordProfileLines.flatMap((profileLine) =>
      profileLine.fixedJoints
        .concat(profileLine.featureJoints)
        .map((webJoint) => webJoint.x)
        .concat(
          [profileLine.baseJointGuid, profileLine.endJointGuid].map(
            (jointGuid) => profile.profileJoints.find(({ guid }) => guid === jointGuid)!.location.x,
          ),
        ),
    ),
    distanceEquals,
  );
  const pitchBreakAndExistingBottomChordWebJointXs = pitchBreakXsInternalToLine.concat(existingBottomChordWebJointXs);
  const pitchBreakXsWithMinimumSpacing = pitchBreakXsInternalToLine.filter((pitchBreakX) =>
    pitchBreakAndExistingBottomChordWebJointXs
      .filter((x) => !distanceEquals(x, pitchBreakX))
      .every((otherWebJoint) => distanceGreaterThanOrEqual(Math.abs(otherWebJoint - pitchBreakX), 18)),
  );

  const isSymmetrical = getIsSymmetrical(profileWithNextLine);

  // If the truss is symmetrical, only add joints to the left half of the truss, and we will mirror the rest later.
  const midpointX = getMidpointX(profileWithNextLine);
  const pitchBreakXCandidates = isSymmetrical
    ? pitchBreakXsWithMinimumSpacing.filter((pitchBreakX) => distanceLessThanOrEqual(pitchBreakX, midpointX))
    : pitchBreakXsWithMinimumSpacing;

  const pitchBreakXsToAddAJointFor: number[] = [];
  for (const pitchBreakX of pitchBreakXCandidates) {
    const draftWebJointSegmentPoints = sortBy(
      uniqWith(existingBottomChordWebJointXs.concat(pitchBreakXsToAddAJointFor), distanceEquals),
      identity,
    );

    // Only place a pitch break joint if it is at least half of the default bottom chord max panel length away from
    // other joints.
    const pitchBreakIsTooCloseToExistingJoint = draftWebJointSegmentPoints.some((existingBottomChordJointX) =>
      distanceLessThanOrEqual(
        Math.abs(existingBottomChordJointX - pitchBreakX),
        profileWithNextLine.bottomChordMaxPanelLength * 0.5,
      ),
    );
    if (pitchBreakIsTooCloseToExistingJoint) continue;

    pitchBreakXsToAddAJointFor.push(pitchBreakX);
  }

  // If the truss is symmetrical, we only found the pitch break joints for the left half of the truss. To maintain
  // symmetry, we will apply web joints in the appropriate mirrored pitch breaks on the right side.
  if (isSymmetrical) {
    const rightSideMirroredPitchBreakJoints = pitchBreakXsToAddAJointFor
      .filter((xLocation) => !distanceEquals(xLocation, midpointX))
      .map((xLocation) => midpointX + (midpointX - xLocation));
    pitchBreakXsToAddAJointFor.push(...rightSideMirroredPitchBreakJoints);
  }

  return pitchBreakXsToAddAJointFor
    .filter((pitchBreakX) => bottomChordProfileLineSpan.containsOnInside(pitchBreakX))
    .map((x) => ({ guid: crypto.randomUUID(), x }));
}

/**
 * This is the client-side version of `Profile.GetPitchBreakXs` on the server.
 */
function getPitchBreakXs(profile: Profile) {
  const topChordProfileLinesGeometries = profile.profileLines
    .filter((profileLine) => profileLine.profileLineType === 'TopChord')
    .map((profileLine) => calculateProfileLineGeometry(profile, profileLine));
  const sortedTopChordProfileLines = sortBy(
    topChordProfileLinesGeometries,
    (profileLineWithGeometry) => profileLineWithGeometry.midpoint.x,
  );
  const topChordPitchBreaks: number[] = uniqWith(
    pairwise(sortedTopChordProfileLines)
      .map(([currentTopChord, nextTopChord]) =>
        currentTopChord.direction.isParallelTo(nextTopChord.direction) ? null : currentTopChord.endPoint.x,
      )
      .filter(notNullish),
    distanceEquals,
  );

  return topChordPitchBreaks;
}

/**
 * This is the client-side version of `Profile.IsSymmetrical` on the server.
 */
function getIsSymmetrical(profileWithNextLine: Profile) {
  const midpointX = getMidpointX(profileWithNextLine);
  const midpointXSliceLine = new Line2D(new Point2D(midpointX, 0), Direction2D.up);

  const profilePolygon = Polygon2D.fromVerticesIfValid(
    uniqWith(
      profileWithNextLine.profileLines.flatMap(
        (profileLine) => calculateProfileLineGeometry(profileWithNextLine, profileLine).endPoints,
      ),
      (pointOne, pointTwo) => pointOne.equals(pointTwo),
    ),
  );
  if (profilePolygon === null) return false;
  const profilePolygonHalves = sortBy(profilePolygon.slice(midpointXSliceLine), (polygon) => polygon.centroid.x);
  if (profilePolygonHalves.length !== 2) return false;
  const leftPolygon = profilePolygonHalves[0];
  const rightPolygon = profilePolygonHalves[1];
  const rightPolygonMirroredPoints = rightPolygon.vertices
    .toReversed()
    .map((vertex) => vertex.to3D().mirrorAcross(midpointXSliceLine.to3D()).projectTo2D());
  const rightPolygonMirrored = Polygon2D.fromVerticesIfValid(rightPolygonMirroredPoints);
  if (rightPolygonMirrored === null) return false;
  return leftPolygon.equals(rightPolygonMirrored);
}

/**
 * This is the client-side version of `Profile.MidpointX` on the server.
 */
function getMidpointX(profile: Profile) {
  const allXs = profile.profileLines.flatMap((profileLine) =>
    calculateProfileLineGeometry(profile, profileLine).endPoints.map((endPoint) => endPoint.x),
  );
  const minX = Math.min(...allXs);
  const maxX = Math.max(...allXs);
  const span = maxX - minX;
  return minX + span / 2;
}
